import { CloudApiError, describeError } from "../lib/errors.js";

const RAW_BASE_URL =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_CLOUD_API_BASE_URL) ||
  "http://127.0.0.1:3001";

export const CLOUD_API_BASE_URL = RAW_BASE_URL.replace(/\/$/, "");

export type AdminTokenAccessor = () => string | null;

let tokenAccessor: AdminTokenAccessor = () => null;
let onUnauthorized: () => void = () => {};

export function configureCloudApiClient(options: {
  getAdminToken?: AdminTokenAccessor;
  onUnauthorized?: () => void;
}): void {
  if (options.getAdminToken) tokenAccessor = options.getAdminToken;
  if (options.onUnauthorized) onUnauthorized = options.onUnauthorized;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  formData?: FormData;
  search?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Whether to send admin bearer token. Defaults to true. */
  withAdminAuth?: boolean;
}

export async function request<TResponse>(path: string, options: RequestOptions = {}): Promise<TResponse> {
  const url = new URL(path, `${CLOUD_API_BASE_URL}/`);
  if (options.search) {
    for (const [key, value] of Object.entries(options.search)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = {};
  if (options.body !== undefined && !options.formData) {
    headers["content-type"] = "application/json";
  }
  if (options.withAdminAuth !== false) {
    const token = tokenAccessor();
    if (token) headers["authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: options.method ?? "GET",
    headers,
    body:
      options.formData ??
      (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    signal: options.signal
  });

  if (response.status === 401) {
    onUnauthorized();
  }

  if (!response.ok) {
    let code = "internal_error";
    try {
      const data = (await response.json()) as { error?: string };
      if (typeof data.error === "string") code = data.error;
    } catch {
      // body is not JSON
    }
    throw new CloudApiError(code, response.status);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }
  return (await response.json()) as TResponse;
}

export { describeError };
